import Image from 'next/image'
import { getReadableImageUrl } from "@/lib/directus";
import { sanitizeHTML } from "@/lib/utils";
import Link from 'next/link';

interface LongLastingResultsData {
  heading?: {
    text?: string;
    title?: string;
  };
  text?: {
    text?: string;
  };
  button?: {
    label?: string;
    href?: string;
  };
  gallery?: {
    id: string;
    site_content_collections_id: {
      collection: {
        collection: string;
        item: {
          // site_content
          title: string;
          
          // menu_items
          href: string;
          label: string;

          // images
          image_file: {
            image: {
              id: string;
              filename_download: string;
            };
          };
        };
      }[];
    };
  }[];
}

export const fields = [
  "heading.text",
  "heading.title",
  "text.text",
  "button.label",
  "button.href",
  "gallery.id",
  "gallery.site_content_collections_id.collection.collection",
  "gallery.site_content_collections_id.collection.item:site_content.title",
  "gallery.site_content_collections_id.collection.item:images.image_file.image.id",
  "gallery.site_content_collections_id.collection.item:images.image_file.image.filename_download",
  "gallery.site_content_collections_id.collection.item:menu_items.href",
  "gallery.site_content_collections_id.collection.item:menu_items.label",
] as const;

interface LongLastingResultsProps {
  data?: LongLastingResultsData;
}

export default function LongLastingResults({ data }: LongLastingResultsProps) {
  // Fallback gallery data if no data is provided
  const galleryFallback = [
    {
      title: 'Primary\nRhinoplasty',
      image: '/images/result-1.png',
      href: '/rhinoplasty/primary-rhinoplasty/',
    },
    {
      title: 'Teen\nRhinoplasty',
      image: '/images/result-2.png',
      href: '/rhinoplasty/teen-rhinoplasty/',
    },
    {
      title: 'Septoplasty',
      image: '/images/result-3.png',
      href: '/rhinoplasty/septoplasty/',
    },
    {
      title: 'Ethnic\nRhinoplasty',
      image: '/images/result-4.png',
      href: '/rhinoplasty/ethnic-rhinoplasty/',
    },
    {
      title: 'Preservation\nRhinoplasty',
      image: '/images/result-5.png',
      href: '/rhinoplasty/preservation-rhinoplasty/',
    },
    {
      title: 'Reconstructive\nRhinoplasty',
      image: '/images/result-6.png',
      href: '/rhinoplasty/reconstructive-rhinoplasty/',
    },
  ]

  // Process gallery data from Directus or use fallback
  const galleryItems = data?.gallery?.map(item => {
    const titleItem = item?.site_content_collections_id?.collection.find(c => c.collection === "site_content")?.item;
    const imageItem = item?.site_content_collections_id?.collection.find(c => c.collection === "images")?.item;
    const menuItem = item?.site_content_collections_id?.collection.find(c => c.collection === "menu_items")?.item;
    
    const title = titleItem?.title || '';
    const imageId = imageItem?.image_file?.image?.id || '';
    const imageFilename = imageItem?.image_file?.image?.filename_download || '';
    const href = menuItem?.href || '#';
    
    return {
      title,
      image: imageId ? getReadableImageUrl(imageId, imageFilename) : '',
      href,
    };
  }) || galleryFallback;

  return (
    <section className="bg-white px-8 xl:px-16 py-8 xl:py-16">
      <div className="container mx-auto">
        {/* Header */}
        <div className="mb-9 md:mb-16">
          <h2 className="text-[2rem] font-essonnes font-light leading-tight md:text-5xl lg:text-[3.375rem] text-primary mb-6 md:mb-10">
            {data?.heading?.text || data?.heading?.title || ""}
          </h2>
          <div 
            className="text-[1rem] font-normal leading-normal md:text-xl text-primary font-greycliff md:leading-7 tracking-tight [&>*]:text-[1rem] [&>*]:font-normal [&>*]:leading-normal md:[&>*]:text-xl md:[&>*]:leading-7"
            dangerouslySetInnerHTML={sanitizeHTML(data?.text?.text || "")}
          />
        </div>

        {/* Gallery Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-4 mb-9 md:mb-16">
          {galleryItems.map((item, index) => (
            <Link key={index} href={item.href} className="block">
              <div className="relative group overflow-hidden rounded-[0.6rem] bg-[#dac79b] transition-all duration-300 hover:shadow-lg">
                <div className="relative aspect-[170/178] md:aspect-[278/292]">
                  <Image
                    src={item.image}
                    alt={item.title}
                    fill
                    className="object-cover"
                    sizes="(max-width: 768px) 170px, 278px"
                    quality={90}
                  />
                  <div className="absolute inset-0 bg-white/60 group-hover:bg-white/40 transition-all duration-300" />
                  <div className="absolute inset-0 flex items-center justify-center p-4">
                    <h3 
                      className="text-center text-xl md:text-[1.5rem] text-primary font-greycliff font-medium md:leading-[2.38rem] tracking-tight whitespace-pre-line"
                      dangerouslySetInnerHTML={sanitizeHTML(item.title || "")}
                    />
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* CTA Button */}
        <div className="flex md:justify-start justify-center w-full">
            <a href={data?.button?.href || "#"} className="inline-block rounded-[3.125rem] bg-primary px-9 py-4 font-greycliff text-xl text-white transition-all duration-300 hover:bg-light hover:text-primary w-full md:w-auto">
                {data?.button?.label || ""}
            </a>
        </div>
      </div>
    </section>
  )
} 